import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { ArrowRight, Zap, Eye, Shield, CheckCircle, Star } from "lucide-react";

import { API } from "../config";

const GALLERY_IMAGES = [
  { url: "https://images.unsplash.com/photo-1772567732993-3c546ee1a52c?w=800&q=80", label: "Virtuve" },
  { url: "https://images.unsplash.com/photo-1772567732989-1ddce59230e4?w=800&q=80", label: "Virtuve" },
  { url: "https://images.unsplash.com/photo-1697946594607-04d755acff2b?w=800&q=80", label: "Virtuve" },
  { url: "https://images.unsplash.com/photo-1719474815477-d765d3d42c9b?w=800&q=80", label: "Telpas" },
  { url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80", label: "Vannas istaba" },
  { url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80", label: "Dzīvojamā istaba" },
];

const TESTIMONIALS = [
  {
    name: "Māris Kalniņš",
    location: "Cēsis",
    text: "Lieliski darbi! Kalkulators palīdzēja saprast cenas jau pirms darbu sākšanas. RMD komanda strādāja kvalitatīvi un ātri.",
    rating: 5,
  },
  {
    name: "Inga Ozola",
    location: "Rīga",
    text: "Virtuves renovācija pagāja bez stresā. Reālas cenas, solītais termiņš, tīra darba vieta. Iesaku visiem!",
    rating: 5,
  },
  {
    name: "Jānis Bērziņš",
    location: "Valmiera",
    text: "Izmantoju kalkulatoru vannas istabas renovācijai. Galīgā cena bija ļoti tuva aprēķinātajai. Profesionāļi!",
    rating: 5,
  },
  {
    name: "Anna Liepiņa",
    location: "Cēsis",
    text: "Pilnīgs dzīvokļa remonts 60m². Viss – no demolēšanas līdz gala tīrīšanai. Ieteiktu katram!",
    rating: 5,
  },
];

export default function HomePage() {
  const { t, lang } = useLanguage();
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
  const [formStatus, setFormStatus] = useState("");

  useEffect(() => {
    axios.get(`${API}/rmd/v1/testimonials`).then(r => {
      if (r.data && r.data.length > 0) setTestimonials(r.data);
    }).catch(() => {});
  }, []);

  const submitTestimonial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/testimonials`, form);
      setTestimonials(prev => [...prev, { ...form, id: Date.now() }]);
      setFormStatus("ok");
      setForm({ name: "", location: "", rating: 5, text: "" });
      setTimeout(() => { setShowForm(false); setFormStatus(""); }, 2500);
    } catch { setFormStatus("error"); }
  };

  return (
    <div style={{ paddingTop: 0 }}>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden bg-black"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1400&q=60)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <div className="max-w-3xl fade-up">
            <span className="rmd-label text-[#FF6600] block mb-4">{t("hero.label")}</span>
            <h1
              className="text-white mb-6 leading-none"
              style={{
                fontFamily: "Chivo, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(2.8rem, 8vw, 7rem)",
                letterSpacing: "-0.04em",
                whiteSpace: "pre-line",
              }}
              data-testid="hero-title"
            >
              {t("hero.title")}
            </h1>
            <p className="text-white/70 text-lg md:text-xl mb-10 max-w-xl fade-up fade-up-delay-1">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 fade-up fade-up-delay-2">
              <Link to="/kalkulators" data-testid="hero-cta-btn" className="rmd-btn-primary">
                {t("hero.cta")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
        {/* Bottom accent */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FF6600]" />
      </section>

      {/* FEATURES */}
      <section data-testid="features-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="rmd-label block mb-4">{t("features.title")}</span>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: Eye, title: t("features.f1t"), desc: t("features.f1d"), delay: "0" },
              { icon: Zap, title: t("features.f2t"), desc: t("features.f2d"), delay: "0.1s" },
              { icon: Shield, title: t("features.f3t"), desc: t("features.f3d"), delay: "0.2s" },
            ].map(({ icon: Icon, title, desc, delay }) => (
              <div
                key={title}
                data-testid="feature-card"
                className="border-2 border-black p-8"
                style={{ boxShadow: "4px 4px 0 #000", animationDelay: delay }}
              >
                <div
                  className="w-12 h-12 bg-[#FF6600] flex items-center justify-center mb-5 border-2 border-black"
                  style={{ boxShadow: "2px 2px 0 #000" }}
                >
                  <Icon size={22} color="black" />
                </div>
                <h3
                  className="text-xl font-black mb-3 uppercase tracking-tight"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="py-20 md:py-28 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="rmd-label text-[#FF6600] block mb-4">{t("howItWorks.title")}</span>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            {[
              { title: t("howItWorks.s1t"), desc: t("howItWorks.s1d") },
              { title: t("howItWorks.s2t"), desc: t("howItWorks.s2d") },
              { title: t("howItWorks.s3t"), desc: t("howItWorks.s3d") },
            ].map(({ title, desc }, i) => (
              <div key={i} data-testid="step-card" className="relative">
                <div className="text-[120px] font-black text-white/5 absolute -top-8 -left-2 leading-none select-none" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {i + 1}
                </div>
                <div className="relative z-10 pt-4">
                  <div className="w-8 h-1 bg-[#FF6600] mb-5" />
                  <h3 className="text-white text-xl font-black mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section data-testid="gallery-preview-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="rmd-label block mb-2">{t("gallery.title")}</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight uppercase leading-none" style={{ fontFamily: "Chivo, sans-serif" }}>
                {t("gallery.subtitle")}
              </h2>
            </div>
            <Link to="/galerija" data-testid="gallery-view-all-btn" className="rmd-btn-secondary hidden md:flex">
              {t("gallery.viewAll")} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {GALLERY_IMAGES.map((img, i) => (
              <div
                key={i}
                data-testid="gallery-preview-item"
                className={`overflow-hidden border-2 border-black ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
                style={{ minHeight: i === 0 ? 320 : 200 }}
              >
                <img
                  src={img.url}
                  alt={img.label}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  style={{ display: "block", minHeight: "100%", height: "100%" }}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 md:hidden">
            <Link to="/galerija" className="rmd-btn-secondary w-full text-center flex justify-center">
              {t("gallery.viewAll")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-[#f4f4f5]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <span className="rmd-label">{t("testimonials.title")}</span>
            <button
              data-testid="add-testimonial-btn"
              onClick={() => setShowForm(v => !v)}
              className="rmd-btn-secondary text-xs"
            >
              <Star size={13} />
              {lang === "ru" ? "Оставить отзыв" : lang === "en" ? "Leave a review" : "Atstāt atsauksmi"}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t_, i) => (
              <div key={t_.id || i} data-testid="testimonial-card"
                className="bg-white border-2 border-black p-6" style={{ boxShadow: "3px 3px 0 #000" }}>
                <div className="flex gap-1 mb-4">
                  {[...Array(t_.rating || 5)].map((_, j) => (
                    <Star key={j} size={14} fill="#FF6600" color="#FF6600" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">"{t_.text}"</p>
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>{t_.name}</p>
                  <p className="text-xs text-gray-400">{t_.location}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testimonial form */}
          {showForm && (
            <div data-testid="testimonial-form" className="mt-8 bg-white border-2 border-black p-6" style={{ boxShadow: "4px 4px 0 #FF6600" }}>
              <h3 className="font-black text-lg uppercase mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
                {lang === "ru" ? "Ваш отзыв" : lang === "en" ? "Your Review" : "Jūsu atsauksme"}
              </h3>
              {formStatus === "ok" ? (
                <div className="flex items-center gap-3 text-green-600 py-4">
                  <CheckCircle size={22} /> <span className="font-semibold">{lang === "ru" ? "Спасибо! Отзыв получен." : lang === "en" ? "Thank you! Review received." : "Paldies! Atsauksme saņemta."}</span>
                </div>
              ) : (
                <form onSubmit={submitTestimonial} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="rmd-label block mb-1">{lang === "ru" ? "Имя" : lang === "en" ? "Name" : "Vārds"} *</label>
                      <input required data-testid="testimonial-name"
                        value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder={lang === "ru" ? "Ваше имя" : lang === "en" ? "Your name" : "Jūsu vārds"}
                        className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
                    </div>
                    <div>
                      <label className="rmd-label block mb-1">{lang === "ru" ? "Город" : lang === "en" ? "City" : "Pilsēta"}</label>
                      <input data-testid="testimonial-location"
                        value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                        placeholder="Cēsis, Rīga..."
                        className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
                    </div>
                  </div>
                  <div>
                    <label className="rmd-label block mb-2">{lang === "ru" ? "Оценка" : lang === "en" ? "Rating" : "Vērtējums"}</label>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} type="button" onClick={() => setForm(f => ({ ...f, rating: s }))}>
                          <Star size={22} fill={form.rating >= s ? "#FF6600" : "none"} color="#FF6600" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="rmd-label block mb-1">{lang === "ru" ? "Отзыв" : lang === "en" ? "Review" : "Atsauksme"} *</label>
                    <textarea required data-testid="testimonial-text" rows={3}
                      value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                      placeholder={lang === "ru" ? "Ваш отзыв о работе RMD..." : lang === "en" ? "Your review of RMD..." : "Jūsu atsauksme par RMD..."}
                      className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] resize-none" />
                  </div>
                  {formStatus === "error" && <p className="text-red-500 text-sm">Kļūda nosūtot atsauksmi</p>}
                  <div className="flex gap-3">
                    <button type="submit" data-testid="testimonial-submit" className="rmd-btn-primary text-sm">
                      <Star size={14} /> {lang === "ru" ? "Отправить" : lang === "en" ? "Submit" : "Nosūtīt"}
                    </button>
                    <button type="button" onClick={() => setShowForm(false)} className="rmd-btn-secondary text-sm">
                      {lang === "ru" ? "Отмена" : lang === "en" ? "Cancel" : "Atcelt"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 md:py-28 bg-[#FF6600]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2
              className="text-4xl md:text-6xl font-black tracking-tight uppercase leading-none mb-4 text-black"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {t("cta.title")}
            </h2>
            <p className="text-black/70 text-lg">{t("cta.subtitle")}</p>
          </div>
          <Link
            to="/kalkulators"
            data-testid="cta-calc-btn"
            className="bg-black text-white font-black text-sm uppercase tracking-wider px-10 py-4 border-2 border-black hover:bg-white hover:text-black transition-colors whitespace-nowrap"
            style={{ fontFamily: "Chivo, sans-serif", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            {t("cta.btn")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <span
              className="text-white font-black text-2xl tracking-tighter block mb-1"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              RMD
            </span>
            <span className="text-white/40 text-xs">Real Men Do · Cēsis, Latvija</span>
          </div>
          <div className="flex flex-wrap gap-6 text-white/50 text-xs">
            <a href="mailto:info@realmendo.com" className="hover:text-[#FF6600] transition-colors no-underline">
              info@realmendo.com
            </a>
            <span>+371 25878923</span>
          </div>
          <div className="text-white/30 text-xs">© {new Date().getFullYear()} RMD</div>
        </div>
      </footer>
    </div>
  );
}
